import {createContext, useReducer} from "react";

/**
 * Stan wyceny — odwzorowanie odpowiedzi /letter-configuration-price.
 *
 * Kwoty priceGross / priceNett są PO RABACIE (rabat nakłada serwer, per symbol,
 * przed mnożeniem przez ilość). grossBeforeDiscount / nettBeforeDiscount to
 * kwoty katalogowe. Sklep nie nakłada rabatu drugi raz.
 */
const initData = {
    priceGross: 0,
    priceNett: 0,
    grossBeforeDiscount: 0,
    nettBeforeDiscount: 0,
    vatRate: null,
    discountPercent: 0,
    items: [],
    lines: [],
    unavailable: false,
    allowAddToCart: false,
    isPriceLoading: false,
}

const round2 = (value) => parseFloat((Number(value) || 0).toFixed(2));

export const PriceContext = createContext(null);
export const PriceDispatch = createContext(null);
export const PriceProvider = ({children}) => {
    const [data, dispatch] = useReducer(
        PriceReducer,
        initData
    )
    return (
        <PriceContext.Provider value={data}>
            <PriceDispatch.Provider value={dispatch}>
                {children}
            </PriceDispatch.Provider>
        </PriceContext.Provider>
    )
}

/**
 * Obiekt `price` zapisywany w konfiguracji — ten sam kształt, który buduje
 * konfigurator na karcie produktu (buildPriceObject w motywie). Sklep czyta
 * z niego cenę koszyka oraz zapisuje items/lines w meta `_litery_wycena`.
 *
 * Z linii zostaje tekst i liczba znaków bez spacji — ceny per linia nie
 * istnieją, bo identyczne litery liczone są przez wszystkie linie naraz.
 */
export function buildPriceExport(price) {
    return {
        priceGross: round2(price.priceGross),
        priceNett: round2(price.priceNett),
        grossBeforeDiscount: round2(price.grossBeforeDiscount),
        nettBeforeDiscount: round2(price.nettBeforeDiscount),
        vatRate: price.vatRate ?? null,
        discountPercent: price.discountPercent ?? 0,
        items: price.items || [],
        lines: (price.lines || []).map((line) => ({
            Tekst: line.Tekst,
            characterCount: line.characterCount,
        })),
        allowAddToCart: true,
    };
}

/** Łączna liczba znaków (bez spacji) we wszystkich liniach. */
export function totalCharacterCount(price) {
    return (price.lines || []).reduce((sum, line) => sum + (parseInt(line.characterCount, 10) || 0), 0);
}

function PriceReducer(data, action) {
    switch (action.type) {
        case "setPricing": {
            const pricing = action.payload || {};
            const totals = pricing.totals || {};
            const lines = Array.isArray(pricing.lines) ? pricing.lines : [];

            return {
                ...data,
                priceGross: round2(totals.gross),
                priceNett: round2(totals.net),
                grossBeforeDiscount: round2(totals.grossBeforeDiscount),
                nettBeforeDiscount: round2(totals.netBeforeDiscount),
                vatRate: pricing.vatRate ?? null,
                discountPercent: Number(pricing.discount) || 0,
                items: Array.isArray(pricing.items) ? pricing.items : [],
                lines,
                unavailable: lines.some((line) => !!line.unavailable),
            };
        }
        case "resetPrice":
            return {
                ...data,
                priceGross: 0,
                priceNett: 0,
                grossBeforeDiscount: 0,
                nettBeforeDiscount: 0,
                items: [],
                lines: [],
                unavailable: false,
            }
        case "setAllowAddToCart":
            return {
                ...data,
                allowAddToCart: action.payload
            }
        case "setPriceLoading":
            return {
                ...data,
                isPriceLoading: action.payload
            }
        default:
            throw Error('Unknown action: ' + action.type);
    }
}
